import React, { useEffect, useState, useRef } from "react";
import { Bar } from "react-chartjs-2";
import { Chart, CategoryScale, LinearScale, Title, BarElement, Tooltip } from "chart.js";
import { loadingCard } from "../../../helpers/loading";

Chart.register(CategoryScale, LinearScale, BarElement, Title, Tooltip);

interface InteractionsBarChartProps {
  data: any;
  activeCard: string;
  isActive?: boolean;
}

export const InteractionsBarChart = ({ data, activeCard = "", isActive = false }: InteractionsBarChartProps) => {
  const [fetchedData, setFetchedData] = useState<any>();
  const chartRef = useRef<any>(null);

  useEffect(() => {
    if (data) {
      const { notes } = data;
      if (activeCard === "total" || activeCard === "") {
        const creators = notes?.creator_counts || [];
        setFetchedData(
          creators.map((c: any) => ({
            created_by: c.created_by,
            creator_firstname: c.firstname,
            creator_lastname: c.lastname,
            count: parseInt(c.count),
          })),
        );
      } else {
        //this is to shape the data for each of the note types
        type CreatorCount = {
          created_by: number;
          creator_firstname: string;
          creator_lastname: string;
          count: number;
        };

        type NoteTypeMap = Record<string, CreatorCount[]>;

        const creatorsByType = notes.creator_note_type_totals || [];
        const mapCreator: NoteTypeMap = {};
        for (const creator of creatorsByType) {
          for (const [noteType, count] of Object.entries(creator.totals)) {
            if (!mapCreator[noteType]) {
              mapCreator[noteType] = [];
            }
            mapCreator[noteType].push({
              created_by: creator.created_by,
              creator_firstname: creator.creator_firstname,
              creator_lastname: creator.creator_lastname,
              count: parseInt(count as string),
            });
          }
        }
        const mapped = mapCreator[activeCard];
        setFetchedData(mapped);
      }
    } else {
      setFetchedData([]);
    }
  }, [data, activeCard]);

  // Trigger a resize when the tab becomes active
  useEffect(() => {
    if (!isActive || !chartRef.current) return;
    const inst = chartRef.current?.chart || chartRef.current;
    if (typeof inst.resize === "function") inst.resize();
  }, [isActive]);

  const chartData = {
    labels:
      fetchedData?.length > 0
        ? fetchedData?.map((item: any) => item.creator_firstname + " " + item.creator_lastname)
        : ["No data available"],
    datasets: [
      {
        label: "Interactions",
        data: fetchedData?.map((item: any) => item.count),
        backgroundColor: ["#5D82A0"],
      },
    ],
  };

  const maxCount = fetchedData?.length ? Math.max(...fetchedData?.map((item: any) => item.count)) : 0;
  const maxCountWithBuffer = Math.ceil(maxCount / 5) * 5;

  const options = {
    indexAxis: "y" as const,
    maintainAspectRatio: false,
    datasets: {
      bar: {
        maxBarThickness: 30,
      },
    },
    scales: {
      x: {
        max: maxCountWithBuffer,
        ticks: {
          callback: (value: number | string) => {
            if (Number.isInteger(value)) return value;
          },
          font: {
            size: 14,
          },
        },
      },
      y: {
        beginAtZero: true,
        max: maxCount + 50,
        ticks: {
          font: {
            size: 14,
          },
        },
      },
    },
    plugins: {
      title: {
        display: true,
        text: "Case Manager Interactions",
        color: "#5a7082",
        font: {
          size: 18,
          weight: 500,
        },
      },
      legend: {
        display: false,
      },
    },
    elements: {
      bar: {
        borderWidth: 1,
        borderRadius: 4,
        barPercentage: 0.8,
        categoryPercentage: 0.9,
      },
    },
  };

  if (!fetchedData) {
    return loadingCard();
  }

  return (
    <div style={{ height: `${chartData.labels.length * 50}px`, minHeight: "150px" }}>
      {chartData.labels.length > 0 && <Bar ref={chartRef} data={chartData} options={options} />}
    </div>
  );
};
